import React, { useRef, useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

const applySafariVideoAttrs = (video) => {
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
};

const waitForVideoReady = (video, timeoutMs = 6000) =>
  new Promise((resolve) => {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      resolve(true);
      return;
    }

    let settled = false;

    const finish = (ready) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("error", onError);
      resolve(ready);
    };

    const onReady = () => finish(true);
    const onError = () => finish(false);
    const timer = setTimeout(() => finish(false), timeoutMs);

    video.addEventListener("loadeddata", onReady);
    video.addEventListener("canplay", onReady);
    video.addEventListener("error", onError);
    video.load();
  });

const pauseOtherUploadVideos = (currentVideo) => {
  document.querySelectorAll(".upload-video-preview video").forEach((video) => {
    if (video !== currentVideo && !video.paused) {
      video.pause();
    }
  });
};

const VideoPreviewLightbox = ({ src, onClose }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    pauseOtherUploadVideos(video);

    const playVideo = async () => {
      applySafariVideoAttrs(video);
      await waitForVideoReady(video);

      try {
        video.muted = false;
        await video.play();
      } catch {
        video.muted = true;
        await video.play().catch(() => {});
      }
    };

    playVideo();

    return () => {
      video.pause();
    };
  }, [src]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="video-preview-lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Video preview"
    >
      <button
        type="button"
        className="video-preview-lightbox-close"
        onClick={onClose}
        aria-label="Close video preview"
      >
        <i className="fa fa-xmark" />
      </button>
      <div
        className="video-preview-lightbox-content"
        onClick={(event) => event.stopPropagation()}
      >
        <video
          ref={videoRef}
          src={src}
          className="video-preview-lightbox-video"
          controls
          playsInline
          preload="auto"
        />
      </div>
    </div>,
    document.body
  );
};

VideoPreviewLightbox.propTypes = {
  src: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

const VideoPreviewThumbnail = ({
  src,
  poster = null,
  variant = "grid",
  videoClassName = "",
  overlayClassName = "",
  playIconClassName = "",
}) => {
  const videoRef = useRef(null);
  const [showLightbox, setShowLightbox] = useState(false);

  const overlayClasses =
    variant === "compact"
      ? `caption-item-thumbnail-overlay upload-video-preview-overlay ${overlayClassName}`.trim()
      : `file-preview-overlay upload-video-preview-overlay ${overlayClassName}`.trim();

  const playIconClasses =
    variant === "compact"
      ? `fa fa-play caption-item-thumbnail-play ${playIconClassName}`.trim()
      : `fa fa-play file-preview-play ${playIconClassName}`.trim();

  const handleOverlayClick = useCallback((event) => {
    event.stopPropagation();
    setShowLightbox(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    applySafariVideoAttrs(video);
  }, [src]);

  const closeLightbox = useCallback(() => {
    setShowLightbox(false);
  }, []);

  return (
    <div className="upload-video-preview">
      <video
        ref={videoRef}
        src={src}
        poster={poster || undefined}
        className={videoClassName}
        muted
        playsInline
        preload="auto"
      />

      {poster && !showLightbox && (
        <img
          src={poster}
          alt=""
          className={`upload-video-poster ${videoClassName}`.trim()}
          aria-hidden="true"
        />
      )}

      {!showLightbox && (
        <button
          type="button"
          className={overlayClasses}
          onClick={handleOverlayClick}
          aria-label="Play video"
        >
          <i className={playIconClasses} />
        </button>
      )}

      {showLightbox && (
        <VideoPreviewLightbox src={src} onClose={closeLightbox} />
      )}
    </div>
  );
};

VideoPreviewThumbnail.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  variant: PropTypes.oneOf(["grid", "compact"]),
  videoClassName: PropTypes.string,
  overlayClassName: PropTypes.string,
  playIconClassName: PropTypes.string,
};

export default VideoPreviewThumbnail;
